<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useGroupStore } from '../stores/groupStore';
import { useVideoStore } from '../stores/videoStore';
import { fetchGroupDetail, uploadFile } from '../composables/useApi';
import type { GroupSummary } from '../types';

const groupStore = useGroupStore();
const videoStore = useVideoStore();

const activeTabId = ref<string>('all');
const showCreateDialog = ref(false);
const showRenameDialog = ref(false);
const newGroupName = ref('');
const newGroupDesc = ref('');
const renamingGroup = ref<GroupSummary | null>(null);
const renameValue = ref('');
const creating = ref(false);
const renaming = ref(false);
const tabScrollRef = ref<HTMLDivElement | null>(null);

// Selection state for batch operations
const selectedFileIds = ref<Set<string>>(new Set());

// Upload state
const uploading = ref(false);
const uploadProgress = ref(0);
const fileInputRef = ref<HTMLInputElement | null>(null);

// Group detail for management
const groupVideoIds = ref<string[]>([]);
const groupVideoCount = ref(0);
const groupLoading = ref(false);

// Group actions menu
const showGroupMenu = ref(false);
document.addEventListener('click', () => { showGroupMenu.value = false; });

const tabs = computed(() => {
  const all: GroupSummary = {
    id: 'all',
    name: '全部',
    description: '',
    videoCount: groupStore.customGroups.reduce((sum, g) => sum + g.videoCount, 0),
    type: 'custom',
  };
  return [all, ...groupStore.customGroups];
});

const activeTab = computed(() => tabs.value.find((t) => t.id === activeTabId.value) || tabs.value[0]);

// Whether we're in group management mode (not "all")
const isManaging = computed(() => activeTabId.value !== 'all');

// Emit selected file IDs to parent
const emit = defineEmits<{
  'selection-change': [fileIds: string[]];
  'upload-files': [files: File[]];
}>();

async function selectTab(tab: GroupSummary) {
  activeTabId.value = tab.id;
  selectedFileIds.value.clear();
  groupVideoIds.value = [];

  if (tab.id === 'all') {
    (videoStore as any).activeGroupFilter = undefined;
    videoStore.applyFilterAndSort();
  } else {
    // Load group detail for file IDs
    groupLoading.value = true;
    try {
      const detail = await fetchGroupDetail(tab.id);
      groupVideoIds.value = detail.videos.map((v: any) => v.file_id);
      groupVideoCount.value = detail.videos.length;
      (videoStore as any).activeGroupFilter = { id: tab.id, type: 'custom', fileIds: groupVideoIds.value };
    } catch {
      (videoStore as any).activeGroupFilter = undefined;
    } finally {
      groupLoading.value = false;
    }
    videoStore.applyFilterAndSort();
  }

  emitSelectionChange();
  await nextTick();
  const el = tabScrollRef.value?.querySelector(`[data-tab-id="${tab.id}"]`) as HTMLElement;
  el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
}

function emitSelectionChange() {
  emit('selection-change', [...selectedFileIds.value]);
}

function toggleSelectAll() {
  if (selectedFileIds.value.size === groupVideoIds.value.length && groupVideoIds.value.length > 0) {
    // 全选 → 全不选
    selectedFileIds.value.clear();
  } else if (selectedFileIds.value.size === 0) {
    // 全不选 → 全选
    selectedFileIds.value = new Set(groupVideoIds.value);
  } else {
    // 部分选 → 反选（已选的取消，未选的勾选）
    const newSet = new Set<string>();
    for (const fid of groupVideoIds.value) {
      if (!selectedFileIds.value.has(fid)) {
        newSet.add(fid);
      }
    }
    selectedFileIds.value = newSet;
  }
  emitSelectionChange();
}

async function handleCreate() {
  if (!newGroupName.value.trim()) return;
  creating.value = true;
  try {
    await groupStore.createGroup(newGroupName.value.trim(), newGroupDesc.value.trim());
    showCreateDialog.value = false;
    newGroupName.value = '';
    newGroupDesc.value = '';
    await nextTick();
    selectTab({ id: newGroupName.value.trim().toLowerCase().replace(/\s+/g, '-'), name: newGroupName.value.trim(), description: '', videoCount: 0, type: 'custom' });
  } catch (e: unknown) {
    alert('创建失败: ' + (e instanceof Error ? e.message : String(e)));
  } finally {
    creating.value = false;
  }
}

async function handleBatchRemove() {
  if (selectedFileIds.value.size === 0) return;
  const count = selectedFileIds.value.size;
  if (!confirm(`确定将 ${count} 个视频从此分组移出？（不会删除视频文件）`)) return;
  try {
    await groupStore.batchRemoveVideos(activeTabId.value, [...selectedFileIds.value]);
    await selectTab(activeTab.value);
  } catch (e: unknown) {
    alert('移出失败: ' + (e instanceof Error ? e.message : String(e)));
  }
}

async function startRename(group: GroupSummary) {
  renamingGroup.value = group;
  renameValue.value = group.name;
  showRenameDialog.value = true;
}

async function handleRename() {
  if (!renamingGroup.value || !renameValue.value.trim()) return;
  renaming.value = true;
  try {
    const oldId = renamingGroup.value.id;
    await groupStore.createGroup(renameValue.value.trim(), renamingGroup.value.description);
    await groupStore.deleteGroup(oldId);
    showRenameDialog.value = false;
    renamingGroup.value = null;
  } catch (e: unknown) {
    alert('重命名失败: ' + (e instanceof Error ? e.message : String(e)));
  } finally {
    renaming.value = false;
  }
}

async function handleDelete() {
  const tab = activeTab.value;
  if (tab.id === 'all') return;
  if (!confirm(`确定删除分组 "${tab.name}"？此操作不可恢复。`)) return;
  try {
    await groupStore.deleteGroup(tab.id);
    if (activeTabId.value === tab.id) {
      selectTab(tabs.value[0]);
    }
  } catch (e: unknown) {
    alert('删除失败: ' + (e instanceof Error ? e.message : String(e)));
  }
}

async function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (!files || files.length === 0) return;

  uploading.value = true;
  uploadProgress.value = 0;
  try {
    for (let i = 0; i < files.length; i++) {
      uploadProgress.value = Math.round(((i + 1) / files.length) * 100);
      try {
        const result = await uploadFile(files[i]);
        if (result.ok && result.file_id) {
          await groupStore.addVideoToGroup(activeTabId.value, result.file_id!);
        }
      } catch (e: unknown) {
        console.error(`Upload failed for ${files[i].name}:`, e);
      }
    }
    // Reload group after upload
    await selectTab(activeTab.value);
  } catch (e: unknown) {
    alert('上传失败: ' + (e instanceof Error ? e.message : String(e)));
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
    input.value = '';
  }
}

const MIME_ICONS: Record<string, string> = {
  mp4: '📺',
  mkv: '🎬',
  webm: '🔥',
};

function getAutoChipIcon(slug: string): string {
  return MIME_ICONS[slug] || '🎥';
}

defineExpose({ isManaging });
</script>

<template>
  <div class="album-section">
    <!-- Tab bar -->
    <div class="tab-bar-wrapper">
      <div ref="tabScrollRef" class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab-pill"
          :class="{ active: activeTabId === tab.id }"
          @click="selectTab(tab)"
          data-tab-id="tab.id"
        >
          <span class="tab-label">{{ tab.name }}</span>
          <span class="tab-count">{{ tab.videoCount }}</span>
        </button>
        <button class="tab-add" @click="showCreateDialog = true" title="新建分组">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Group management toolbar (shown when managing a group) -->
    <div v-if="isManaging && !groupLoading" class="group-toolbar">
      <div class="toolbar-left">
        <label class="select-all-btn">
          <input type="checkbox" :checked="selectedFileIds.size === groupVideoIds.length && groupVideoIds.length > 0" :indeterminate="selectedFileIds.size > 0 && selectedFileIds.size < groupVideoIds.length" @change="toggleSelectAll()" />
          <span>全选</span>
        </label>
        <span v-if="selectedFileIds.size > 0" class="sel-count">已选 {{ selectedFileIds.size }} / {{ groupVideoIds.length }}</span>
      </div>
      <div class="toolbar-right">
        <label class="upload-btn" :class="{ uploading: uploading }">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          上传
          <input type="file" ref="fileInputRef" accept="video/*,.mkv,.webm,.avi,.mov,.wmv" multiple @change="handleFileUpload" style="display:none" />
        </label>
        <button class="batch-remove-btn" :disabled="selectedFileIds.size === 0" @click="handleBatchRemove">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          移出 ({{ selectedFileIds.size }})
        </button>
        <button class="group-actions-btn" @click.stop="showGroupMenu = !showGroupMenu">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Group actions dropdown -->
    <div v-if="isManaging && showGroupMenu" class="group-menu" @click.stop>
      <button class="menu-item" @click="startRename(activeTab)">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
        </svg>
        重命名分组
      </button>
      <button class="menu-item menu-item-danger" @click="handleDelete">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        删除分组
      </button>
    </div>

    <!-- Upload progress -->
    <div v-if="uploading" class="upload-progress">
      <div class="progress-bar"><div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div></div>
      <span class="progress-text">上传中 {{ uploadProgress }}%</span>
    </div>

    <!-- Auto group chips -->
    <div v-if="(!isManaging || groupLoading) && (groupStore.dateGroups.length || groupStore.typeGroups.length)" class="auto-chips">
      <span
        v-for="g in [...groupStore.dateGroups, ...groupStore.typeGroups]"
        :key="g.id + g.type"
        class="auto-chip"
        :class="'chip-' + g.type"
        @click="videoStore.setSearch(''); (videoStore as any).activeGroupFilter = { id: g.id, type: g.type }; videoStore.applyFilterAndSort();"
      >
        <span class="chip-icon">{{ g.type === 'date' ? '📅' : getAutoChipIcon(g.id) }}</span>
        <span class="chip-name">{{ g.name }}</span>
        <span class="chip-count">({{ g.videoCount }})</span>
      </span>
    </div>

    <!-- Create dialog -->
    <Teleport to="body">
      <div v-if="showCreateDialog" class="modal-backdrop" @click="showCreateDialog = false">
        <div class="modal-dialog" @click.stop>
          <h3 class="modal-title">新建分组</h3>
          <div class="form-group">
            <label>分组名称</label>
            <input v-model="newGroupName" placeholder="例如：旅行记录、教程合集" autofocus @keyup.enter="handleCreate" />
          </div>
          <div class="form-group">
            <label>描述（可选）</label>
            <textarea v-model="newGroupDesc" placeholder="分组描述" rows="2"></textarea>
          </div>
          <div class="modal-actions">
            <button class="modal-btn modal-btn-cancel" @click="showCreateDialog = false">取消</button>
            <button class="modal-btn modal-btn-primary" :disabled="creating || !newGroupName.trim()" @click="handleCreate">
              {{ creating ? '创建中...' : '创建' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Rename dialog -->
    <Teleport to="body">
      <div v-if="showRenameDialog" class="modal-backdrop" @click="showRenameDialog = false">
        <div class="modal-dialog" @click.stop>
          <h3 class="modal-title">重命名分组</h3>
          <div class="form-group">
            <label>新名称</label>
            <input v-model="renameValue" autofocus @keyup.enter="handleRename" />
          </div>
          <div class="modal-actions">
            <button class="modal-btn modal-btn-cancel" @click="showRenameDialog = false">取消</button>
            <button class="modal-btn modal-btn-primary" :disabled="renaming || !renameValue.trim()" @click="handleRename">
              {{ renaming ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.album-section {
  margin-bottom: 24px;
  width: 100%;
  position: relative;
}

/* ── Tab bar ── */
.tab-bar-wrapper {
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 2px;
}
.tab-bar-wrapper::-webkit-scrollbar { display: none; }

.tab-bar {
  display: flex;
  gap: 6px;
  padding: 4px 0;
  width: max-content;
}

.tab-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 100px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}
.tab-pill:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-soft);
}
.tab-pill.active {
  background: var(--accent-gradient);
  color: white;
  border-color: transparent;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.3);
}
.tab-pill.active .tab-count {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.tab-label { line-height: 1; }

.tab-count {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 100px;
  background: rgba(99, 102, 241, 0.15);
  color: var(--accent);
  line-height: 1;
}

.tab-add {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 100px;
  border: 1px dashed var(--border-color);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}
.tab-add:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-soft);
}

/* ── Group toolbar ── */
.group-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  flex-wrap: wrap;
  gap: 8px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  user-select: none;
}
.select-all-btn input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--accent);
  cursor: pointer;
}

.sel-count {
  font-size: 12px;
  color: var(--accent);
  font-weight: 600;
}

.upload-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--accent);
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all var(--transition-fast);
}
.upload-btn:hover:not(.uploading) {
  opacity: 0.9;
  transform: translateY(-1px);
}
.upload-btn.uploading {
  opacity: 0.7;
  cursor: wait;
}

.batch-remove-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--danger-soft);
  color: var(--danger);
  border: 1px solid transparent;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all var(--transition-fast);
}
.batch-remove-btn:hover:not(:disabled) {
  background: var(--danger);
  color: white;
}
.batch-remove-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.group-actions-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all var(--transition-fast);
}
.group-actions-btn:hover {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

/* ── Group menu dropdown ── */
.group-menu {
  position: absolute;
  top: 100%;
  right: 12px;
  margin-top: 4px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 4px;
  z-index: 100;
  box-shadow: var(--shadow-elevated);
  min-width: 160px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  border-radius: 6px;
  transition: all var(--transition-fast);
}
.menu-item:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}
.menu-item-danger {
  color: var(--danger);
}
.menu-item-danger:hover {
  background: var(--danger-soft);
}

/* ── Upload progress ── */
.upload-progress {
  padding: 8px 0;
  margin-bottom: 8px;
}

.progress-bar {
  height: 4px;
  background: var(--bg-tertiary);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--accent-gradient);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  display: block;
  text-align: center;
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 4px;
}

/* ── Auto chips ── */
.auto-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.auto-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 12px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  border: 1px solid transparent;
  background: var(--bg-tertiary);
}
.auto-chip:hover {
  color: var(--text-secondary);
  background: var(--bg-secondary);
}
.auto-chip.chip-date:hover { border-color: #f59e0b; color: #f59e0b; }
.auto-chip.chip-type:hover { border-color: #10b981; color: #10b981; }

.chip-icon { font-size: 13px; }
.chip-name { font-weight: 500; }
.chip-count { font-size: 11px; }

/* ── Modals ── */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease-out;
}

.modal-dialog {
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 24px;
  width: 400px;
  max-width: 90vw;
  animation: fadeInUp 0.2s ease-out;
  box-shadow: var(--shadow-elevated);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 20px 0;
}

.form-group { margin-bottom: 14px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  transition: border-color var(--transition-fast);
  box-sizing: border-box;
}
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent);
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.modal-btn {
  padding: 8px 20px;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  border: none;
}

.modal-btn-cancel {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}
.modal-btn-cancel:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.modal-btn-primary {
  background: var(--accent);
  color: white;
}
.modal-btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}
.modal-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .album-section {
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .tab-pill {
    padding: 6px 12px;
    font-size: 13px;
  }

  .group-toolbar {
    padding: 8px 10px;
  }

  .toolbar-right {
    gap: 6px;
  }

  .upload-btn,
  .batch-remove-btn {
    padding: 5px 10px;
    font-size: 12px;
  }

  .modal-dialog {
    padding: 16px;
    width: calc(100vw - 32px);
  }
}
</style>
